/**
 * Client-side env schema. Validated at boot; fail fast with clear errors.
 * Only VITE_* vars are safe in the client bundle.
 */

import { z } from 'zod';

const isLocalDev = import.meta.env.DEV;

const supabaseUrlSchema = z
  .string()
  .min(1, 'VITE_SUPABASE_URL is required')
  .url('VITE_SUPABASE_URL must be a valid URL')
  .refine(
    (url) => {
      try {
        const u = new URL(url);
        // Allow localhost / 127.0.0.1 in dev mode
        if (isLocalDev && (u.hostname === 'localhost' || u.hostname === '127.0.0.1')) {
          return true;
        }
        return u.hostname.endsWith('.supabase.co') && u.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'VITE_SUPABASE_URL must be https://*.supabase.co (or localhost in dev mode)' }
  );

const publishableKeySchema = z
  .string()
  .min(1, 'VITE_SUPABASE_PUBLISHABLE_KEY is required');

const projectIdSchema = z
  .string()
  .regex(/^[a-z0-9]{20}$/, 'VITE_SUPABASE_PROJECT_ID must match Supabase project ref (20 alphanumeric)');

const envSchema = z.object({
  VITE_SUPABASE_URL: supabaseUrlSchema,
  VITE_SUPABASE_PUBLISHABLE_KEY: publishableKeySchema,
  VITE_SUPABASE_PROJECT_ID: z
    .string()
    .optional()
    .refine((v) => {
      if (v === undefined || v === '') return true;
      // Allow "local" in dev mode for local Supabase
      if (isLocalDev && v === 'local') return true;
      return /^[a-z0-9]{20}$/.test(v);
    }, {
      message: 'VITE_SUPABASE_PROJECT_ID must match Supabase project ref (20 alphanumeric)',
    }),
});

type EnvInput = {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
  VITE_SUPABASE_PROJECT_ID?: string;
};

function getClientEnv(): EnvInput {
  return {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    VITE_SUPABASE_PROJECT_ID: import.meta.env.VITE_SUPABASE_PROJECT_ID,
  };
}

const parsed = envSchema.safeParse(getClientEnv());

if (!parsed.success) {
  const issues = parsed.error.issues;
  const lines = issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`);
  throw new Error(
    [
      'Invalid client env:',
      ...lines,
      '',
      'Cloudflare Pages env vars missing or misconfigured.',
      'Set these keys in Cloudflare Pages → <your project> → Settings → Environment variables:',
      '  - VITE_SUPABASE_URL',
      '  - VITE_SUPABASE_PUBLISHABLE_KEY',
      '  - VITE_SUPABASE_PROJECT_ID (optional but recommended, must be 20 lowercase letters/numbers)',
      '',
      'For local dev, set them in .env.local (see .env.example).',
    ].join('\n')
  );
}

if (import.meta.env.DEV) {
  const presentKeys = Object.entries(parsed.data)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key]) => key);
  // Log keys only, never values
  // eslint-disable-next-line no-console
  console.info('[env] Loaded client env keys:', presentKeys);
}

export const env = parsed.data;

export type ClientEnv = z.infer<typeof envSchema>;
